import React, {useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {toAbsoluteUrl} from '../../../../../_gori/helpers'
import {KTSVG} from '../../../../../_gori/helpers'
import {Button} from '../../../../../_gori/partials/widgets'
import {numericOnly} from '../../core/shippingCalculatorHelpers'

const USPS_DIM_FACTOR_LEGACY = 166
const USPS_DIM_FACTOR_NEW = 139
const USPS_NEW_DIVISOR_EFFECTIVE = new Date('2026-07-12T00:00:00-07:00')
const UPS_FEDEX_DIM_FACTOR = 139
const OZ_PER_LB = 16

const DIMS = ['length', 'width', 'height'] as const

const UNIT_OPTIONS = ['lb', 'oz'] as const
type WeightUnit = typeof UNIT_OPTIONS[number]
type CarrierKey = 'usps' | 'ups' | 'fedex'

const CARRIERS: ReadonlyArray<{key: CarrierKey; logo: string}> = [
  {key: 'usps', logo: '/media/gori/carrier-logos/usps.png'},
  {key: 'ups', logo: '/media/gori/carrier-logos/ups.png'},
  {key: 'fedex', logo: '/media/gori/carrier-logos/fedex.png'},
]

/** Resolve the active USPS divisor for the given moment (transition-aware). */
const getUspsDimFactor = (now: Date = new Date()): number =>
  now >= USPS_NEW_DIVISOR_EFFECTIVE ? USPS_DIM_FACTOR_NEW : USPS_DIM_FACTOR_LEGACY

/**
 * Per-carrier billable DIM weight in the target unit. This is a pure DIM
 * calculator: it always returns a value regardless of carrier-specific
 * billing rules (e.g. USPS's 1-cubic-foot floor is intentionally NOT applied).
 */
const calcDimWeight = (
  cubicInches: number,
  carrier: CarrierKey,
  unit: WeightUnit,
  now: Date = new Date()
): number => {
  const factor = carrier === 'usps' ? getUspsDimFactor(now) : UPS_FEDEX_DIM_FACTOR
  const lbs = cubicInches / factor
  return unit === 'oz' ? Math.ceil(lbs * OZ_PER_LB) : Math.ceil(lbs)
}

export function DimCalculator() {
  const intl = useIntl()

  const [dims, setDims] = useState({length: '', width: '', height: ''})
  const [result, setResult] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [unit, setUnit] = useState<WeightUnit>('lb')
  const [unitOpen, setUnitOpen] = useState(false)
  const unitRef = useRef<HTMLDivElement>(null)

  // Close dropdown on click outside (same pattern as ShippingCostCalculator)
  useEffect(() => {
    if (!unitOpen) return
    const handler = (e: MouseEvent) => {
      if (unitRef.current && !unitRef.current.contains(e.target as Node)) setUnitOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [unitOpen])

  const calculate = () => {
    setResult(null)
    setError(null)
    const l = parseFloat(dims.length)
    const w = parseFloat(dims.width)
    const h = parseFloat(dims.height)
    if ([l, w, h].some((v) => isNaN(v) || v <= 0)) {
      return setError(intl.formatMessage({id: 'ERROR_FILL_DIMENSIONS'}))
    }
    // Store raw cubic inches; per-carrier DIM weight computed at render time
    setResult(l * w * h)
  }

  const handleUnitSelect = (u: WeightUnit) => {
    setUnit(u)
    setUnitOpen(false)
  }

  return (
    <div className='d-flex flex-column gap-6 align-items-center w-100'>
      <div className='shipping-calc__tool-desc-box'>
        <h3 className='fw-bolder fs-4 mb-4'>{intl.formatMessage({id: 'DIM_WEIGHT_CALCULATOR'})}</h3>
        <p className='fs-6 mb-0 text-gray-700'>
          {intl.formatMessage({id: 'DIM_WEIGHT_CALCULATOR_DESC'})}
        </p>
      </div>

      {/* Calculator */}
      <form
        className='shipping-calc__tool-input-box d-flex flex-column gap-6'
        onSubmit={(e) => {
          e.preventDefault()
          calculate()
        }}
      >
        {/* Dimensions */}
        <div className='shipping-calc__dim-container'>
          <label className='fw-bolder fs-4 lh-base shipping-calc__input-title'>
            {intl.formatMessage({id: 'DIMENSIONS'})}
          </label>
          <div className='d-flex gap-3'>
            {DIMS.map((d) => (
              <div key={d} className='position-relative flex-grow-1'>
                <span className='shipping-calc__dim-label fs-7 fw-bold' aria-hidden='true'>
                  {d[0].toUpperCase()}
                </span>
                <input
                  type='text'
                  inputMode='decimal'
                  value={dims[d]}
                  placeholder='0'
                  className='shipping-calc__dim-input bg-white shipping-calc__border-radius-6'
                  aria-label={`${intl.formatMessage({id: 'DIMENSIONS'})} ${d}`}
                  onChange={(e) => {
                    if (numericOnly(e.target.value)) {
                      setDims((p) => ({...p, [d]: e.target.value}))
                      if (result !== null) setResult(null)
                      if (error) setError(null)
                    }
                  }}
                />
                <span className='shipping-calc__dim-unit fs-7 fw-bold' aria-hidden='true'>
                  {intl.formatMessage({id: 'IN'})}
                </span>
              </div>
            ))}
          </div>
        </div>

        <Button
          classNameDiv='w-100'
          className='shipping-calc__calculate-btn w-100 fs-4 border-0 text-white d-flex justify-content-center align-items-center'
          submit
          label={intl.formatMessage({id: 'CALCULATE'})}
        />
      </form>
      {error && (
        <div className='shipping-calc__error-module' role='alert'>
          {error}
        </div>
      )}
      {result !== null && (
        <div className='shipping-calc__dim-result' aria-live='polite'>
          <div className='shipping-calc__dim-result-label'>
            <span className='shipping-calc__dim-result-title'>
              {intl.formatMessage({id: 'DIM_WEIGHT'})}
            </span>
            {/* Unit selector with chevron-down dropdown */}
            <div className='shipping-calc__unit-selector' ref={unitRef}>
              <button
                type='button'
                className='shipping-calc__unit-toggle'
                onClick={() => setUnitOpen((prev) => !prev)}
                aria-haspopup='listbox'
                aria-expanded={unitOpen}
                aria-label={intl.formatMessage({id: 'DIM_WEIGHT_UNIT'})}
              >
                <span>{unit}</span>
                <KTSVG
                  path='/media/gori/arrows/arrow_down.svg'
                  className='shipping-calc__unit-chevron'
                  svgClassName='w-16px h-16px'
                />
              </button>
              {unitOpen && (
                <ul className='shipping-calc__unit-dropdown' role='listbox'>
                  {UNIT_OPTIONS.map((u) => (
                    <li
                      key={u}
                      role='option'
                      aria-selected={u === unit}
                      className={`shipping-calc__unit-option${u === unit ? ' active' : ''}`}
                      onClick={() => handleUnitSelect(u)}
                    >
                      {u}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className='shipping-calc__dim-result-carriers'>
            {CARRIERS.map((c) => (
              <div key={c.key} className='shipping-calc__dim-result-carrier'>
                <img
                  src={toAbsoluteUrl(c.logo)}
                  alt={c.key.toUpperCase()}
                  className='shipping-calc__dim-result-logo'
                />
                <div className='shipping-calc__dim-result-weight'>
                  <span className='shipping-calc__dim-result-number'>
                    {calcDimWeight(result, c.key, unit)}
                  </span>
                  <span className='shipping-calc__dim-result-oz'>{unit.toUpperCase()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
